import React, { useState } from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  Stack,
  styled,
  TextField,
  Typography,
} from '@worthy-npm/worthy-common-ui-components';
import AddressInput, { AddressForAutocomplete } from '../inputs/addressInput/addressInput';
import { TextInput } from '../inputs/addressInput/addressInput.styles';
import {
  createShipping,
  getAddress,
  getCreateShippingLoading,
  getScheduleShippingLoading,
  getShippingMethod,
  getUserPhone,
  scheduleShipping,
  setSchedulingPhoneNumber,
  setShippingMethod,
  toShowPhoneInput,
} from '../../slices/schedulingSlice';
import { SchedulingType } from '../../types/scheduling';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { normalizePhoneNumber, validatePhoneNumber } from '../../lib/validations/phoneNumber';
import WorthyAPI from '../../services/worthyAPI';
import { selectItemPublicId } from '../../slices/submitSlice';
import ErrorState from './ErrorState';
import GA from '../../data/GA';
import { redirectToMyItems } from '../../lib/redirectUtils';
import NextButton from './NextButton';

const RadioWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  paddingLeft: '8px',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

function Address({ nextStep }: { nextStep: () => void }) {
  const dispatch = useAppDispatch();
  const {
    street: streetFromState,
    city: cityFromState,
    state: stateFromState,
    zipcode: zipcodeFromState,
    privateResidence: privateResidenceFromState,
    apt: aptFromState,
  } = useAppSelector(getAddress);
  const itemPublicId = useAppSelector(selectItemPublicId);
  const userPhone = useAppSelector(getUserPhone);
  const shippingMethod = useAppSelector(getShippingMethod);
  // const userPhone = '1234567890';
  const showPhoneInput = useAppSelector(toShowPhoneInput);
  // const showPhoneInput = false;
  const createShippingLoading = useAppSelector(getCreateShippingLoading);
  const scheduleShippingLoading = useAppSelector(getScheduleShippingLoading);
  const [isPrivateResidence, setIsPrivateResidence] = useState(privateResidenceFromState);
  const [phoneNumber, setPhoneNumber] = useState(userPhone || '');
  const [apt, setApt] = useState(aptFromState || '');
  const [street, setStreet] = useState(streetFromState || '');
  const [city, setCity] = useState(cityFromState || '');
  const [state, setState] = useState(stateFromState || '');
  const [zipcode, setZipcode] = useState(zipcodeFromState || '');
  const [zipcodeError, setZipcodeError] = useState('');
  const [stateError, setStateError] = useState('');
  const [cityError, setCityError] = useState('');
  const [showZipcodeInput, setShowZipcodeInput] = useState(false);
  const [showStateInput, setShowStateInput] = useState(false);
  const [showCityInput, setShowCityInput] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showErrorState, setShowErrorState] = useState(false);

  const setAddress = (address: AddressForAutocomplete) => {
    setStreet(address.street);
    if (!address.zipcode) {
      setZipcode('');
      setShowZipcodeInput(true);
    } else {
      setShowZipcodeInput(false);
      setZipcode(address.zipcode);
    }
    if (!address.state) {
      setState('');
      setShowStateInput(true);
    } else {
      setShowStateInput(false);
      setState(address.state);
    }
    if (!address.city) {
      setCity('');
      setShowCityInput(true);
    } else {
      setShowCityInput(false);
      setCity(address.city);
    }
    setZipcodeError('');
    setStateError('');
    setCityError('');
  };
  const validateZipcode = (value: string) => {
    const zipcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    if (!zipcodeRegex.test(value)) {
      setZipcodeError('Invalid zipcode format');
    } else {
      setZipcodeError('');
    }
  };
  const handleZipcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setZipcode(value);
    validateZipcode(value);
  };

  const validateState = (value: string) => {
    if (!value) {
      setStateError('State is required');
    } else {
      setStateError('');
    }
  };
  const validateCity = (value: string) => {
    if (!value) {
      setCityError('City is required');
    } else {
      setCityError('');
    }
  };
  const handleStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setState(value);
    validateState(value);
  };
  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCity(value);
    validateCity(value);
  };
  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length > 14) {
      return;
    }
    const formattedValue = normalizePhoneNumber(value);
    setPhoneNumber(formattedValue);
    const isValid = validatePhoneNumber(value);
    if (!isValid) {
      setPhoneError('Invalid phone number');
    } else {
      setPhoneError('');
    }
  };
  const handleNext = async () => {
    setShowErrorState(false);
    if (itemPublicId) {
      WorthyAPI.logUserAction('Next button clicked - address provided', itemPublicId).catch(
        (err) => {
          console.error(
            `Error logging Next button clicked - address provided user action for item ${itemPublicId}`,
            err,
          );
        },
      );
    }
    if (showPhoneInput) {
      if (itemPublicId) {
        WorthyAPI.logUserAction('Complete Phone number modal - clicked next', itemPublicId).catch(
          (err) => {
            console.error(
              `Error logging Phone number input user action for item ${itemPublicId}`,
              err,
            );
          },
        );
      }
      const res = await dispatch(setSchedulingPhoneNumber(phoneNumber));
      if (res.meta.requestStatus === 'rejected') {
        setShowErrorState(true);
        return;
      }
    }
    const createRes = await dispatch(
      createShipping({
        street,
        street2: isPrivateResidence ? '' : apt,
        city,
        state,
        zipcode,
      }),
    );
    if (createRes.meta.requestStatus === 'fulfilled') {
      // For drop-off, also call scheduleShipping
      if (shippingMethod === SchedulingType.DROPOFF) {
        WorthyAPI.logUserAction('Finish drop-off button clicked', itemPublicId).catch((err) => {
          console.error(
            `Error logging Finish drop-off button clicked user action for item ${itemPublicId}`,
            err,
          );
        });
        const scheduleRes = await dispatch(scheduleShipping());
        if (scheduleRes.meta.requestStatus === 'fulfilled') {
          nextStep();
        } else {
          setShowErrorState(true);
        }
      } else {
        // For pickup, just proceed to next step
        nextStep();
      }
    } else {
      setShowErrorState(true);
    }
  };

  const handleRetry = () => {
    setShowErrorState(false);
  };

  const handleDropOffInstead = () => {
    setShowErrorState(false);
    dispatch(setShippingMethod(SchedulingType.DROPOFF));
    WorthyAPI.logUserAction('Drop-off item instead button clicked', itemPublicId).catch((err) => {
      console.error(
        `Error logging Drop-off item instead button clicked user action for item ${itemPublicId}`,
        err,
      );
    });
  };

  return (
    <Stack justifyContent="center" alignItems="center" gap={4}>
      {showErrorState ? (
        <ErrorState
          message={`There seems to be an issue scheduling a ${shippingMethod === SchedulingType.DROPOFF ? 'dropoff' : 'pickup'} at the provided address.`}
          onRetry={handleRetry}
          buttonText={shippingMethod === SchedulingType.PICKUP ? 'Drop-off item instead' : ''}
          onButtonClick={shippingMethod === SchedulingType.PICKUP ? handleDropOffInstead : undefined}
        />
      ) : (
        <>
          <Stack gap={2} width="100%" alignItems="start" justifyContent="center" maxWidth={360}>
            <Stack width="100%" gap={1}>
              {showPhoneInput && (
                <TextInput
                  key="phone-number"
                  id="phone-number"
                  fullWidth
                  label="Phone Number"
                  autoFocus
                  error={!!phoneError}
                  helperText={phoneError}
                  InputLabelProps={{ style: { color: '#7A8994', paddingLeft: '16px' } }}
                  data-automation="phone-input"
                  variant="standard"
                  value={phoneNumber}
                  placeholder="(423) 456-7890"
                  onChange={handlePhoneChange}
                />
              )}
              <AddressInput
                address={{
                  street,
                  city,
                  state,
                  zipcode,
                }}
                setAddress={setAddress}
              />
              {showZipcodeInput && (
                <TextField
                  variant="standard"
                  label="Zipcode"
                  fullWidth
                  value={zipcode}
                  onChange={handleZipcodeChange}
                  error={!!zipcodeError}
                  helperText={zipcodeError}
                  InputLabelProps={{
                    sx: {
                      paddingLeft: 2,
                      color: 'secondary.main',
                    },
                  }}
                />
              )}
              {showStateInput && (
                <TextInput
                  id="address-state"
                  fullWidth
                  label="State"
                  value={state}
                  onChange={handleStateChange}
                  error={!!stateError}
                  helperText={stateError}
                  InputLabelProps={{
                    sx: {
                      paddingLeft: 2,
                      color: 'secondary.main',
                    },
                  }}
                  data-automation="state-input"
                  variant="standard"
                />
              )}
              {showCityInput && (
                <TextInput
                  id="address-city"
                  fullWidth
                  label="City"
                  value={city}
                  onChange={handleCityChange}
                  error={!!cityError}
                  helperText={cityError}
                  InputLabelProps={{
                    sx: {
                      paddingLeft: 2,
                      color: 'secondary.main',
                    },
                  }}
                  data-automation="city-input"
                  variant="standard"
                />
              )}
              <TextInput
                disabled={isPrivateResidence}
                value={apt}
                onChange={(e) => setApt(e.target.value)}
                id="address-apt"
                fullWidth
                label="Apartment, unit, building, floor, etc."
                InputLabelProps={{
                  sx: {
                    paddingLeft: 2,
                    color: 'secondary.main',
                  },
                }}
                data-automation="apt-input"
                variant="standard"
              />
            </Stack>
            <RadioWrapper>
              <FormControlLabel
                label={(
                  <Typography color="text.primary" variant="caption" fontWeight="light">
                    This is a private residence
                  </Typography>
                )}
                data-automation="private-residence"
                id="private-residence"
                checked={isPrivateResidence}
                control={(
                  <Radio
                    sx={{
                      color: 'primary.main',
                    }}
                    size="small"
                    color="primary"
                    onClick={() => {
                      setIsPrivateResidence(!isPrivateResidence);
                      setApt('');
                    }}
                  />
                )}
              />
            </RadioWrapper>
          </Stack>

          <NextButton
            onClick={handleNext}
            disabled={
              !street
              || !zipcode
              || !state
              || !city
              || !phoneNumber
              || !!zipcodeError
              || !!stateError
              || !!cityError
              || !!phoneError
              || (!isPrivateResidence && !apt)
            }
            loading={createShippingLoading || scheduleShippingLoading}
          />
        </>
      )}
    </Stack>
  );
}

export default Address;
